/**
 * PlumBuddy Form Submission Utility
 *
 * Submits signup forms through a Cloudflare Worker proxy so that
 * ad-blockers can't silently drop HubSpot requests from the user's
 * browser. The Worker handles Turnstile verification, honeypot checks,
 * and parallel HubSpot + Klaviyo forwarding with HubSpot as the primary
 * success signal (HubSpot failure = user sees error instead of false
 * "success").
 *
 * Usage per section:
 *   PBForms.submitForm(formEl, {
 *     formType: 'pros_signup',      // one of: contact | pros_signup | homeowner_signup | partner_rebate
 *     workerUrl: '...',             // Worker /submit URL (from section settings)
 *     rules: {...},                 // client-side validation rules
 *     fields: ['first_name', ...],  // form field names to include in submission
 *     onSuccess: fn,
 *     onError: fn
 *   });
 *
 * Turnstile is mandatory. The section must render a widget with id
 * matching `[data-form-turnstile="<formType>"]` so this utility can find
 * and execute it.
 */
window.PBForms = (function () {
  'use strict';

  /* ---- Validation ---- */

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /* ---- Paid attribution helpers ----
     Mirror of contact-form.liquid's helpers. Reads UTM params saved to
     localStorage by the firm's Shopify Customer Events UTM-saver script
     (key "paid_attr", URL-encoded query string), and reads hubspotutk
     from document.cookie. Both fail-soft (return empty) if missing — we
     never want attribution gathering to break a form submission. */
  function readPaidAttrUTM() {
    var empty = { utm_source: '', utm_medium: '', utm_campaign: '', utm_term: '', utm_content: '' };
    try {
      var raw = window.localStorage && window.localStorage.getItem('paid_attr');
      if (!raw) return empty;
      var p = new URLSearchParams(raw);
      return {
        utm_source:   p.get('utm_source')   || '',
        utm_medium:   p.get('utm_medium')   || '',
        utm_campaign: p.get('utm_campaign') || '',
        utm_term:     p.get('utm_term')     || '',
        utm_content:  p.get('utm_content')  || ''
      };
    } catch (e) { return empty; }
  }

  function readHubSpotCookie() {
    var m = (document.cookie || '').match(/(?:^|;\s*)hubspotutk=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  function getFieldValue(formEl, name) {
    var el = formEl.elements[name];
    if (!el) return '';
    if (el.type === 'checkbox') return el.checked;
    return (el.value || '').trim();
  }

  function validateField(formEl, name, rule) {
    var val = getFieldValue(formEl, name);

    var isRequired = rule.required;
    if (!isRequired && rule.requiredIf) {
      var depVal = getFieldValue(formEl, rule.requiredIf.field);
      if (rule.requiredIf.values.indexOf(depVal) !== -1) {
        isRequired = true;
      }
    }

    if (rule.type === 'checkbox') {
      if (isRequired && !val) return 'This field is required';
      return null;
    }

    if (isRequired && !val) return 'This field is required';
    if (!val) return null;

    if (rule.type === 'email' && !EMAIL_RE.test(val)) {
      return 'Please enter a valid email address';
    }

    if (rule.type === 'tel' && val) {
      var digits = val.replace(/\D/g, '');
      var min = rule.minDigits || 10;
      if (digits.length < min) {
        return 'Please enter a valid phone number';
      }
    }

    if (rule.type === 'select' && isRequired && !val) {
      return 'Please select an option';
    }

    return null;
  }

  function validateForm(formEl, rules) {
    var errors = {};
    var valid = true;
    for (var name in rules) {
      var err = validateField(formEl, name, rules[name]);
      if (err) {
        errors[name] = err;
        valid = false;
      }
    }
    return { valid: valid, errors: errors };
  }

  /* ---- UI Helpers ---- */

  function showFieldError(formEl, name, message) {
    var el = formEl.elements[name];
    if (!el) return;
    var container = el.closest('.form-group') || el.parentElement;
    if (!container) return;

    if (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA') {
      el.style.borderColor = '#c62828';
    }

    var errEl = container.querySelector('.field-error');
    if (!errEl) {
      errEl = document.createElement('div');
      errEl.className = 'field-error';
      errEl.style.cssText = 'color:#c62828;font-size:13px;margin-top:5px;';
      container.appendChild(errEl);
    }
    errEl.textContent = message;
  }

  function clearFieldError(formEl, name) {
    var el = formEl.elements[name];
    if (!el) return;
    var container = el.closest('.form-group') || el.parentElement;
    if (!container) return;

    if (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA') {
      el.style.borderColor = '';
    }
    var errEl = container.querySelector('.field-error');
    if (errEl) errEl.remove();
  }

  function showAllErrors(formEl, errors) {
    for (var name in errors) {
      showFieldError(formEl, name, errors[name]);
    }
    var firstErrorField = formEl.elements[Object.keys(errors)[0]];
    if (firstErrorField && firstErrorField.scrollIntoView) {
      firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function clearAllErrors(formEl, rules) {
    for (var name in rules) {
      clearFieldError(formEl, name);
    }
  }

  /* ---- Turnstile execute flow ---- */

  /**
   * Execute the Turnstile widget for this form and wait for a token.
   * Resolves with the token string, or rejects on timeout / error.
   */
  function executeTurnstile(widgetEl) {
    return new Promise(function (resolve, reject) {
      if (!window.turnstile) {
        reject(new Error('turnstile_unavailable'));
        return;
      }
      var settled = false;
      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        reject(new Error('turnstile_timeout'));
      }, 15000);

      try {
        window.turnstile.reset(widgetEl);
      } catch (_e) { /* reset throws if not rendered yet — safe to ignore */ }

      // Re-render with a success callback bound to this Promise. Using
      // window.turnstile.render gives us an explicit callback per execute;
      // otherwise multiple forms on a page fight over data-callback.
      try {
        var siteKey = widgetEl.getAttribute('data-sitekey');
        window.turnstile.render(widgetEl, {
          sitekey: siteKey,
          size: 'invisible',
          callback: function (token) {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            resolve(token);
          },
          'error-callback': function () {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            reject(new Error('turnstile_error'));
          }
        });
        window.turnstile.execute(widgetEl);
      } catch (err) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(err);
      }
    });
  }

  /* ---- Submission ---- */

  function submitForm(formEl, config) {
    clearAllErrors(formEl, config.rules);
    var result = validateForm(formEl, config.rules);
    if (!result.valid) {
      showAllErrors(formEl, result.errors);
      return;
    }

    if (!config.workerUrl || !config.formType) {
      console.error('PBForms: missing workerUrl or formType');
      if (config.onError) config.onError(new Error('config'));
      return;
    }

    var widget = formEl.querySelector('[data-form-turnstile]');
    if (!widget) {
      console.error('PBForms: no Turnstile widget found in form');
      if (config.onError) config.onError(new Error('no_widget'));
      return;
    }

    var btn = formEl.querySelector('button[type="submit"]');
    var btnText = btn ? btn.querySelector('.button-text') : null;
    var originalText = btnText ? btnText.textContent : '';
    if (btn) btn.disabled = true;
    if (btnText) btnText.textContent = 'Submitting...';

    function restoreButton() {
      if (btn) btn.disabled = false;
      if (btnText) btnText.textContent = originalText;
    }

    /* Collect form values — only fields declared in config.fields */
    var fields = {};
    (config.fields || []).forEach(function (name) {
      var v = getFieldValue(formEl, name);
      if (typeof v === 'boolean') v = v ? 'true' : 'false';
      if (v) fields[name] = v;
    });

    /* Paid attribution: inject saved UTMs into the allowlist-bound fields
       object. Only include non-empty values so we don't send blanks. The
       worker still drops them if they're not in the form's fieldMap. */
    var utm = readPaidAttrUTM();
    if (utm.utm_source)   fields.utm_source   = utm.utm_source;
    if (utm.utm_medium)   fields.utm_medium   = utm.utm_medium;
    if (utm.utm_campaign) fields.utm_campaign = utm.utm_campaign;
    if (utm.utm_term)     fields.utm_term     = utm.utm_term;
    if (utm.utm_content)  fields.utm_content  = utm.utm_content;

    /* Honeypot value (hidden input named website_url on each form) */
    var honeypot = '';
    var hpEl = formEl.elements['website_url'];
    if (hpEl && hpEl.value) honeypot = hpEl.value;

    executeTurnstile(widget)
      .then(function (token) {
        var payload = {
          form_type: config.formType,
          fields: fields,
          turnstile_token: token,
          website_url: honeypot,
          page_uri: window.location.href,
          page_name: document.title,
          /* HubSpot tracking cookie — worker forwards to HS as context.hutk
             per HS Forms API (NOT a regular field). Empty when HS tracking
             hasn't set the cookie yet; worker drops empty values. */
          hutk: readHubSpotCookie()
        };
        /* Optional per-submission override (e.g. popup variant suffix) */
        if (config.customSource) payload.custom_source = config.customSource;
        return fetch(config.workerUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      })
      .then(function (resp) {
        if (!resp.ok) throw new Error('submission_failed');
        if (config.onSuccess) config.onSuccess();
      })
      .catch(function (err) {
        if (config.onError) config.onError(err);
      })
      .then(restoreButton);
  }

  /* ---- Blur Validation Setup ---- */

  function attachBlurValidation(formEl, rules) {
    for (var name in rules) {
      (function (fieldName) {
        var el = formEl.elements[fieldName];
        if (!el) return;
        var eventName = (el.type === 'checkbox' || el.tagName === 'SELECT') ? 'change' : 'blur';
        el.addEventListener(eventName, function () {
          var err = validateField(formEl, fieldName, rules[fieldName]);
          if (err) {
            showFieldError(formEl, fieldName, err);
          } else {
            clearFieldError(formEl, fieldName);
          }
        });
      })(name);
    }
  }

  return {
    validateForm: validateForm,
    submitForm: submitForm,
    attachBlurValidation: attachBlurValidation
  };
})();
